// ═══════════════════════════════════════════════════════════
// Public Release Page — /portal/release/:token
//
// No login required. Requesters click the link emailed to them
// when staff releases documents. The token identifies the request
// and scopes access to released files only.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { IconDownload, IconFileText, IconAlertCircle, IconLoader2, IconArrowLeft, IconPackage } from '@tabler/icons-react';
import { validateReleaseToken } from '../../api/releaseTokens';
import { getReleasedDocuments, getDownloadUrlForRelease, type FoiaDocument } from '../../api/foiaDocuments';

type PageState = 'loading' | 'invalid' | 'ready' | 'empty';

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function mimeIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return '🖼';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  return '📎';
}

export default function ReleasePage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [docs, setDocs] = useState<FoiaDocument[]>([]);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [zipping, setZipping] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      const validated = await validateReleaseToken(token);
      if (!validated) { setState('invalid'); return; }

      const released = await getReleasedDocuments(validated.requestId, { anonymous: true });
      setDocs(released);
      setState(released.length === 0 ? 'empty' : 'ready');
    })();
  }, [token]);

  async function handleDownload(doc: FoiaDocument) {
    setDownloading(doc.recordId);
    setDownloadError(null);
    try {
      const url = await getDownloadUrlForRelease(doc, { anonymous: true });
      if (!url) throw new Error('Download URL unavailable — please contact the records office.');
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setDownloading(null);
    }
  }

  async function handleDownloadAll() {
    if (docs.length === 0) return;
    setZipping(true);
    setDownloadError(null);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      await Promise.all(docs.map(async doc => {
        try {
          const url = await getDownloadUrlForRelease(doc, { anonymous: true });
          if (!url) return;
          const resp = await fetch(url);
          if (!resp.ok) return;
          const blob = await resp.blob();
          zip.file(doc.fileName, blob);
        } catch (err) {
          console.error('[zip] failed for', doc.fileName, err);
        }
      }));

      const content = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(content);
      a.download = 'released-records.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'ZIP download failed.');
    } finally {
      setZipping(false);
    }
  }

  const shell = (content: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: 'var(--color-neutral-50)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '48px 16px' }}>
      <div style={{ width: '100%', maxWidth: 640 }}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconFileText size={18} style={{ color: '#fff' }} />
          </div>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-neutral-900)' }}>Records Release</span>
        </div>
        {content}
      </div>
    </div>
  );

  if (state === 'loading') return shell(
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--color-neutral-500)', fontSize: 14 }}>
      <IconLoader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
      Verifying link…
    </div>
  );

  if (state === 'invalid') return shell(
    <div style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 10, padding: 32, textAlign: 'center' }}>
      <IconAlertCircle size={36} style={{ color: 'var(--cds-red-700)', marginBottom: 12 }} />
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: 'var(--color-neutral-900)' }}>Link Invalid or Expired</div>
      <div style={{ fontSize: 13, color: 'var(--color-neutral-500)', marginBottom: 24 }}>
        This download link is no longer valid. It may have expired or been revoked. Please contact the records office for assistance.
      </div>
      <Link to="/portal" style={{ fontSize: 13, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <IconArrowLeft size={14} /> Return to Portal
      </Link>
    </div>
  );

  if (state === 'empty') return shell(
    <div style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 10, padding: 32, textAlign: 'center' }}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: 'var(--color-neutral-900)' }}>No Documents Available Yet</div>
      <div style={{ fontSize: 13, color: 'var(--color-neutral-500)' }}>
        Your records are being prepared. You will receive another email when documents are ready to download.
      </div>
    </div>
  );

  return shell(
    <div style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 10, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-neutral-900)', marginBottom: 4 }}>
            Released Records
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-neutral-500)' }}>
            {docs.length} document{docs.length !== 1 ? 's' : ''} available for download
          </div>
        </div>
        {docs.length > 1 && (
          <button
            className="btn btn-primary btn-sm"
            onClick={handleDownloadAll}
            disabled={zipping || !!downloading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {zipping
              ? <><IconLoader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Building ZIP…</>
              : <><IconPackage size={13} /> Download All</>
            }
          </button>
        )}
      </div>

      {/* Notice */}
      <div style={{ padding: '12px 24px', background: 'var(--cds-yellow-50)', borderBottom: '1px solid #FDE68A', fontSize: 12, color: '#92400E' }}>
        <strong>Notice:</strong> Some records may have been redacted pursuant to applicable exemptions under the Freedom of Information Act. The versions available below are the official released copies.
      </div>

      {/* Download error */}
      {downloadError && (
        <div style={{ margin: '12px 24px 0', padding: '10px 14px', background: 'var(--cds-red-50)', border: '1px solid var(--cds-red-200)', borderRadius: 6, fontSize: 12, color: 'var(--cds-red-700)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <IconAlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          {downloadError}
        </div>
      )}

      {/* Document list */}
      <div style={{ padding: '8px 0' }}>
        {docs.map(doc => (
          <div key={doc.recordId} style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            gap: 12,
            padding: '12px 24px',
            borderBottom: '1px solid var(--color-neutral-100)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
              <span style={{ fontSize: 20, lineHeight: 1, marginTop: 2, flexShrink: 0 }}>{mimeIcon(doc.mimeType)}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-neutral-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {doc.fileName}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-neutral-400)', marginTop: 2 }}>
                  {formatBytes(doc.fileSize)}
                  {doc.responsiveClass === 'partiallyResponsive' && (
                    <span style={{ marginLeft: 8, color: 'var(--cds-terracotta-700)', fontWeight: 600 }}>Redacted copy</span>
                  )}
                </div>
              </div>
            </div>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => handleDownload(doc)}
              disabled={downloading === doc.recordId}
              style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
            >
              {downloading === doc.recordId
                ? <><IconLoader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Downloading…</>
                : <><IconDownload size={13} /> Download</>
              }
            </button>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: '16px 24px', borderTop: '1px solid var(--color-neutral-100)', fontSize: 11, color: 'var(--color-neutral-400)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Keep this link for your records. It remains valid for 365 days from the date it was issued.</span>
        <Link to="/portal" style={{ fontSize: 12, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconArrowLeft size={12} /> Portal
        </Link>
      </div>
    </div>
  );
}
